import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';

// Third Party Libraries
import DatePicker from '~/components/General/OlcDatePicker';
import { addDays, getDay } from 'date-fns';

// Utils
import { MESSAGES } from '~/utils/message';
import { dateProtector } from '~/utils/date-format';

// Components
import GenericAutocomplete from '~/components/General/Autocomplete';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import LimitModal from '~/components/General/LimitModal';
import CampaignsMailDateTable from './CampaignsMailDate';
import SingleMailDateTable from './SingleMailDate';

export const addBusinessDays = (startDate, daysToAdd) => {
  // Create a copy of the date to avoid mutating the original object
  let date = new Date(startDate);
  let daysAdded = 0;

  while (daysAdded < daysToAdd) {
    // Add one day to the date
    date.setDate(date.getDate() + 1);

    // getDay() returns 0 for Sunday and 6 for Saturday
    const dayOfWeek = date.getDay();

    // If it is not a weekend (0 or 6), count it as a day added
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysAdded++;
    }
  }

  return date;
};

// MUI Components
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import Input from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Tooltip from '@mui/material/Tooltip';

// Styles
import './styles.scss';
import { ContactInfoCIcon } from '~/assets/images';

const BATCH_OPTIONS = [
  { id: 2, label: '2 Batches' },
  { id: 3, label: '3 Batches' },
  { id: 4, label: '4 Batches' },
  { id: 5, label: '5 Batches' },
  { id: 6, label: '6 Batches' },
  { id: 7, label: '7 Batches' },
  { id: 8, label: '8 Batches' },
];

const OrderDetails = (props) => {
  const {
    setSelectedReturnedAddress,
    selectedReturnedAddress,
    scheduleOptions,
    selectedDate,
    expectedDateData,
    setSelectedDate,
    isDatepickerOpen,
    returnOptions,
    datepickerRef,
    setStartDate,
    startDate,
    setIsDatepickerOpen,
    setSearch,
    setErrors,
    loading,
    errors,
    orderName,
    setOrderName,
    selectionType,
    selectedCampaign,
    selectedTemplate,
    onValidationChange,
    // Batch props
    numberOfBatches = 1,
    setNumberOfBatches,
    daysBetweenBatches = '',
    setDaysBetweenBatches,
    isMultipleContacts = false,
    isBatchingAllowed = true,
  } = props;

  const [mailingSchedule, setMailingSchedule] = useState('send-all-at-once');
  const [showBatchingLimitModal, setShowBatchingLimitModal] = useState(false);
  const hasInitializedDefaultDate = useRef(false);

  // ASAP label:
  // - Temporarily use next day's date in the format "Friday November 28 2025" (no commas)
  // - Ignore API mailedDate here as requested
  const asapLabelWithDate = useMemo(() => {
    // const mailed = expectedDateData?.mailedDate;
    const tomorrow = addBusinessDays(new Date(), 2);
    const weekday = tomorrow.toLocaleDateString('en-US', { weekday: 'long' });
    const month = tomorrow.toLocaleDateString('en-US', { month: 'long' });
    const day = tomorrow.getDate();
    const year = tomorrow.getFullYear();
    const formattedTomorrow = `${weekday} ${month} ${day} ${year}`;

    return `ASAP - ${formattedTomorrow}`;
  }, []);

  // Function to filter out weekends and day after tomorrow
  const isWeekday = (date) => {
    const day = getDay(date);
    const tomorrow = addDays(new Date(), 1);

    // Disable weekends (Saturday = 6, Sunday = 0)
    if (day === 0 || day === 6) {
      return false;
    }

    // Disable tomorrow
    if (date.toDateString() === tomorrow.toDateString()) {
      return false;
    }

    return true;
  };

  const scheduleOptionsWithDate = useMemo(() => {
    // Only update the ASAP label if expectedDateData is available to prevent label changes after render
    return (scheduleOptions || []).map((opt) => {
      if (opt?.id === 1 || opt?.value === 'ASAP') {
        // Use the memoized label which will only update when expectedDateData is available
        return { ...opt, label: asapLabelWithDate };
      }
      // Match the screenshot text for the select-date row
      if (opt?.id === 2) {
        return { ...opt, label: 'Select a Date' };
      }
      return opt;
    });
  }, [scheduleOptions, asapLabelWithDate]);

  const selectedScheduleValue = useMemo(() => {
    if (selectedDate && typeof selectedDate === 'object') {
      if (selectedDate?.id === 1 || selectedDate?.value === 'ASAP') {
        return { ...selectedDate, label: asapLabelWithDate };
      }
      if (selectedDate?.id === 2) {
        return { ...selectedDate, label: 'Select a Date' };
      }
    }
    return selectedDate;
  }, [selectedDate, asapLabelWithDate]);

  // Memoize templateInfo to prevent table reloads when unrelated props change
  const templateInfo = useMemo(() => {
    if (!selectedTemplate) return undefined;
    return {
      label: selectedTemplate?.label || selectedTemplate?.title,
      productType: selectedTemplate?.object?.product?.productType,
      productId: selectedTemplate?.object?.product?.id,
    };
  }, [
    selectedTemplate?.id,
    selectedTemplate?.label,
    selectedTemplate?.title,
    selectedTemplate?.object?.product?.productType,
    selectedTemplate?.object?.product?.id,
  ]);

  // Memoize mailedDate to prevent unnecessary table reloads
  const mailedDate = useMemo(() => {
    return expectedDateData?.mailedDate;
  }, [expectedDateData?.mailedDate]);

  // Memoize mailedDateBreakdown to prevent unnecessary table reloads
  const mailedDateBreakdown = useMemo(() => {
    return expectedDateData?.mailedDateBreakdown || [];
  }, [expectedDateData?.mailedDateBreakdown]);

  // Memoize fallback templates array to prevent unnecessary re-renders
  const fallbackTemplates = useMemo(() => {
    return selectedCampaign?.templates || [];
  }, [selectedCampaign]);

  // Derive per-batch dates for the Mail Date column.
  // Single mailer only: the API returns one mailedDateBreakdown entry per contact batch,
  // so we map breakdown → batchDates directly.
  // Campaigns compute batch dates per-row inside CampaignsMailDateTable (each automation
  // step has its own mailedDate which becomes the Batch-1 base for that row).
  const batchDates = useMemo(() => {
    if (numberOfBatches <= 1) return [];
    if (selectionType?.id === 2) return []; // campaigns handle this per-row

    const breakdown = expectedDateData?.mailedDateBreakdown;
    if (!Array.isArray(breakdown) || breakdown.length === 0) return [];

    return breakdown.map((b, i) => ({
      batch: i + 1,
      date: new Date(b.mailedDate).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    }));
  }, [expectedDateData, numberOfBatches, selectionType?.id]);

  // Set the first option as default only once when scheduleOptionsWithDate first becomes available
  // This prevents re-setting default when user explicitly clears the field
  useLayoutEffect(() => {
    if (
      scheduleOptionsWithDate &&
      scheduleOptionsWithDate.length > 0 &&
      !selectedDate &&
      !hasInitializedDefaultDate.current
    ) {
      setSelectedDate(scheduleOptionsWithDate[0]);
      hasInitializedDefaultDate.current = true;
    }
  }, [scheduleOptionsWithDate, setSelectedDate]);

  const handleMailingScheduleChange = (e) => {
    setMailingSchedule(e.target.value);
  };

  useEffect(() => {
    if (!onValidationChange) return;
    const hasReturnAddress =
      selectedReturnedAddress &&
      selectedReturnedAddress.id &&
      selectedReturnedAddress.id !== '-100';
    const hasDate = !!selectedDate;
    // When batches > 1, days between batches is required
    const hasBatchConfig =
      numberOfBatches <= 1 ||
      (Number(daysBetweenBatches) > 0 && !isNaN(Number(daysBetweenBatches)));
    onValidationChange(hasReturnAddress && hasDate && hasBatchConfig);
  }, [
    selectedReturnedAddress,
    selectedDate,
    onValidationChange,
    numberOfBatches,
    daysBetweenBatches,
  ]);

  return (
    <>
      <Box className="orderDetailsContent" sx={{ flex: '1 1 auto' }}>
        <Box
          className="orderDetailsLayout"
          sx={{
            minHeight: '100%',
            alignItems: 'stretch',
            boxSizing: 'border-box',
          }}
        >
          <Box className="orderDetailsFormColumn">
            <Typography variant="h6" sx={{ marginBottom: '8px' }}>
              Order Details
            </Typography>
            <Box className="orderDetailsReturnAddress">
              <InputLabel className="selectLabel">
                {MESSAGES.ORDERS.CREATE.RETURN_ADDRESS_TITLE_NEW}
                <span className="required">*</span>
                <Tooltip
                  title="The return address on your mailers comes from the sender profile you choose. A sender profile includes your name, company, and address, and can be reused across different orders."
                  placement="bottom-start"
                >
                  <img src={ContactInfoCIcon} alt="info" style={{ cursor: 'pointer' }} />
                </Tooltip>
              </InputLabel>
              <OlcDropdownSearch
                options={returnOptions}
                selectedValue={selectedReturnedAddress}
                setSelectedValue={setSelectedReturnedAddress}
                clearable={true}
                placeHolderText={'Return Address'}
                noOptionText="No options found"
                searchFieldPlaceholderText={'Search Return Address'}
                onSearchChange={setSearch}
                component="returnAddress"
                className="newAutocompleteDesign"
                searchLoading={loading}
                menuPaperClassName="createOrderDropdownMenu"
              />
              {errors.returnAddress && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {MESSAGES.ORDERS.CREATE.RETURN_ADDRESS_REQUIRED}
                </Typography>
              )}
            </Box>
            <Box className="orderDetailsReturnAddress">
              <InputLabel className="selectLabel">
                {MESSAGES.ORDERS.CREATE.DATE_LABEL_NEW2}
                <span className="required">*</span>
              </InputLabel>
              <GenericAutocomplete
                placeholder="Mail Date"
                options={scheduleOptionsWithDate}
                selectedValue={selectedScheduleValue}
                setSelectedValue={setSelectedDate}
                search={() => {}}
                updateErrors={setErrors}
                name={'date'}
                newDesign={true}
                className="newAutocompleteDesign"
                menuClassName="createOrderDropdownMenu"
                hideDividers={true}
              />
              {errors.date && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {MESSAGES.ORDERS.CREATE.DATE_REQUIRED}
                </Typography>
              )}
              {isDatepickerOpen && (
                <Box ref={datepickerRef} className="mailedDate">
                  <Box className="selectDate orderDetailsDatePicker">
                    <DatePicker
                      selected={startDate}
                      minDate={addBusinessDays(new Date(), 2)}
                      filterDate={isWeekday}
                      onChange={(update) => {
                        dateProtector(update);
                        setStartDate(update);
                        setSelectedDate(update.toLocaleDateString('en-US').slice(0, 10));
                        setIsDatepickerOpen(false);
                      }}
                      onFocus={() => setIsDatepickerOpen(true)}
                      inline
                    />
                  </Box>
                </Box>
              )}
              <Typography
                className="mailDateNote"
                sx={{
                  fontSize: '14px !important',
                  color: '#00000099',
                }}
              >
                {`Note: For processing purposes, mailing dates must be scheduled at least 2 days in advance. Please select a date later than today and tomorrow.`}
              </Typography>
              {/* {expectedDateData && selectionType?.id !== 2 && (
                <Box className="expectedDate">
                  <Typography>
                    <strong>{MESSAGES.ORDERS.CREATE.DELIVERY_DATE}</strong>
                    {expectedDateData?.mailedDate}
                  </Typography>
                </Box>
              )} */}
              {/* {expectedDateData && selectionType?.id !== 2 && ( */}
              {/* <Typography className="orderDetailsCancelDate">
              This order can be canceled up until 11:59 PM on Sep 27, 2025
            </Typography> */}
              {/* )} */}
            </Box>
            {/* <Box className="orderDetailsReturnAddress">
            <InputLabel className="selectLabel radioLabel">
              Mailing Schedule
              <Tooltip title="Mailing Schedule">
                <img src={ContactInfoCIcon} alt="info" style={{ cursor: 'pointer' }} />
              </Tooltip>
              <br />
              <a className="labelLink">Learn more</a>
            </InputLabel>
            <FormControl className="orderTypeFormControl">
              <RadioGroup
                className="orderTypeRadioGroup"
                name="order-type"
                value={mailingSchedule}
                onChange={(e) => handleMailingScheduleChange(e)}
              >
                <FormControlLabel
                  className="orderTypeRadioGroupItem"
                  value="send-all-at-once"
                  control={<Radio disableRipple={true} className="orderTypeRadio" />}
                  label="Send All at Once"
                />
                <FormControlLabel
                  className="orderTypeRadioGroupItem"
                  value="send-in-batches"
                  control={<Radio disableRipple={true} className="orderTypeRadio" />}
                  label="Send in Batches"
                />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box className="orderDetailsReturnAddress">
            <InputLabel className="selectLabel">
              Number of Batches
              <span className="required">*</span>
            </InputLabel>
            <GenericAutocomplete
              placeholder="Return Address"
              options={returnOptions}
              setSelectedValue={setSelectedReturnedAddress}
              selectedValue={selectedReturnedAddress}
              search={setSearch}
              updateErrors={setErrors}
              name={'returnAddress'}
              loading={loading}
              newDesign={true}
              className="newAutocompleteDesign"
            />
            {errors.returnAddress && (
              <Typography className="errorMessage">
                <sup>*</sup>
                {MESSAGES.ORDERS.CREATE.RETURN_ADDRESS_REQUIRED}
              </Typography>
            )}
          </Box>
          <Box className="orderDetailsReturnAddress">
            <InputLabel className="selectLabel">
              Number of Days Between Batches
              <span className="required">*</span>
            </InputLabel>
            <Input
              className="orderNameInput"
              placeholder="Order Name"
              value={orderName}
              onChange={(e) => {
                if (e.target.value.length <= 25) {
                  setOrderName(e.target.value);
                }
              }}
              inputProps={{ maxLength: 25 }}
            />
          </Box> */}

            {/* Mailing schedule: single mailer (multi/upload), or campaign once a campaign is selected */}
            {((isMultipleContacts && selectionType?.id !== 2) ||
              (selectionType?.id === 2 && selectedCampaign)) && (
              <>
                <Box className="orderDetailsReturnAddress">
                  <InputLabel className="selectLabel">Mailing Schedule</InputLabel>
                  <RadioGroup
                    className="mailingScheduleGroup"
                    row
                    value={numberOfBatches > 1 ? 'send-in-batches' : 'send-all-at-once'}
                    onChange={(e) => {
                      if (e.target.value === 'send-all-at-once') {
                        setNumberOfBatches(1);
                        setDaysBetweenBatches('');
                      } else {
                        if (!isBatchingAllowed) {
                          setShowBatchingLimitModal(true);
                          return;
                        }
                        setNumberOfBatches((prev) => (prev <= 1 ? 2 : prev));
                      }
                    }}
                  >
                    <FormControlLabel
                      className="mailingScheduleOption"
                      value="send-all-at-once"
                      control={
                        <Radio
                          className="mailingScheduleRadio"
                          disableRipple
                          size="small"
                          sx={{ color: '#ed5c2f', '&.Mui-checked': { color: '#ed5c2f' } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '14px', fontFamily: 'Inter' }}>
                          Send All at Once
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      className="mailingScheduleOption"
                      value="send-in-batches"
                      control={
                        <Radio
                          className="mailingScheduleRadio"
                          disableRipple
                          size="small"
                          sx={{ color: '#ed5c2f', '&.Mui-checked': { color: '#ed5c2f' } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '14px', fontFamily: 'Inter' }}>
                          Send in Batches
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </Box>

                {numberOfBatches > 1 && (
                  <Box className="batchControlsRow">
                    <Box className="orderDetailsReturnAddress">
                      <InputLabel className="selectLabel">
                        Number of Batches
                        <span className="required">*</span>
                      </InputLabel>
                      <GenericAutocomplete
                        placeholder="Select Batches"
                        options={BATCH_OPTIONS}
                        selectedValue={BATCH_OPTIONS.find((o) => o.id === numberOfBatches) || null}
                        setSelectedValue={(val) => setNumberOfBatches(val?.id ?? 2)}
                        search={() => {}}
                        updateErrors={() => {}}
                        newDesign={true}
                        className="newAutocompleteDesign"
                        menuClassName="createOrderDropdownMenu"
                        readOnlyInput={true}
                        hideClearIcon={true}
                        nativeScrollbar={true}
                      />
                    </Box>

                    <Box className="orderDetailsReturnAddress">
                      <InputLabel className="selectLabel">
                        Number of Days Between Batches
                        <span className="required">*</span>
                      </InputLabel>
                      <Input
                        className="orderNameInput"
                        placeholder="e.g. 7"
                        value={daysBetweenBatches}
                        onChange={(e) =>
                          setDaysBetweenBatches(e.target.value.replace(/[^0-9]/g, ''))
                        }
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}

            <Box className="orderDetailsReturnAddress">
              <InputLabel className="selectLabel">{MESSAGES.ORDERS.CREATE.ORDER_NAME}</InputLabel>
              <Input
                className="orderNameInput"
                placeholder="Order Name"
                value={orderName}
                onChange={(e) => {
                  if (e.target.value.length <= 25) {
                    setOrderName(e.target.value);
                  }
                }}
                inputProps={{ maxLength: 25 }}
              />
              <Typography
                className="orderNameLength"
                sx={{
                  color: `${orderName.length >= 25 ? '#ff0000' : '#00000099'}`,
                }}
              >
                {`${orderName.length}/25 characters`}
              </Typography>
            </Box>
          </Box>

          {useMemo(
            () =>
              expectedDateData && (!selectionType || selectionType?.id !== 2) ? (
                <Box
                  className="orderDetailsTableColumn"
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <SingleMailDateTable
                    mailedDate={mailedDate}
                    selectedDate={selectedDate}
                    templateInfo={templateInfo}
                    batchDates={batchDates}
                  />
                </Box>
              ) : null,
            [expectedDateData, selectionType, mailedDate, selectedDate, templateInfo, batchDates]
          )}

          {useMemo(
            () =>
              selectionType?.id === 2 && selectedCampaign ? (
                <Box
                  className="orderDetailsTableColumn"
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <CampaignsMailDateTable
                    mailedDateBreakdown={mailedDateBreakdown}
                    selectedDate={selectedDate}
                    fallbackTemplates={fallbackTemplates}
                    fallbackMailedDate={mailedDate}
                  />
                </Box>
              ) : null,
            [
              selectionType?.id,
              selectedCampaign,
              mailedDateBreakdown,
              selectedDate,
              mailedDate,
              fallbackTemplates,
            ]
          )}
        </Box>
      </Box>
      <LimitModal
        open={showBatchingLimitModal}
        onClose={() => setShowBatchingLimitModal(false)}
        header={MESSAGES.ORDERS.CREATE.BATCHING_NOT_ALLOWED_HEADER}
        message={MESSAGES.ORDERS.CREATE.BATCHING_NOT_ALLOWED}
      />
    </>
  );
};

export default OrderDetails;
